// src/pages/commercial/CommercialClientsPage.js
import React, { useEffect, useState } from 'react';
import CommercialLayout from '../../components/commercial/CommercialLayout';
import { useAuth } from '../../contexts/AuthContext';
import pb from '../../lib/pocketbase';
import { useNavigate } from 'react-router-dom';

export default function CommercialClientsPage() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [clients, setClients] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');

    // Modal & Details
    const [showModal, setShowModal] = useState(false);
    const [showDetail, setShowDetail] = useState(false);
    const [selectedClient, setSelectedClient] = useState(null);

    // Form Data
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        company: '',
        address: '',
        notes: ''
    });

    // Stats for details (mocked or fetched if needed)
    const [detailStats, setDetailStats] = useState({ quotes: [], invoices: [], projects: [] });

    // Timeline functionality
    const [activeTab, setActiveTab] = useState('overview');

    // Share / QR Code
    const [showQr, setShowQr] = useState(false);
    const [qrUrl, setQrUrl] = useState('');
    const companyName = 'INFOCOLAB';
    const buildShareText = (link) => (
        `Bonjour,\n\n` +
        `pour créer votre dossier client et démarrer votre projet, merci de compléter ce formulaire : ${link}\n` +
        `Cela prend moins de 3 minutes.\n` +
        `Si vous avez la moindre question, répondez simplement à ce message — nous sommes là pour vous accompagner.\n\n` +
        `Bien à vous,\n` +
        `L’équipe ${companyName}`
    );

    const fetchClients = async () => {
        setLoading(true);
        try {
            const res = await pb.collection('customers').getFullList({
                sort: '-created',
                filter: `created_by = "${user.id}" || commercial_id = "${user.id}"`
            });
            setClients(res);
        } catch (err) {
            console.error("Error fetching clients", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchClients();
    }, []);

    const fetchClientDetails = async (client) => {
        if (!client) return;
        try {
            const [qRes, iRes, pRes] = await Promise.all([
                pb.collection('quotes').getList(1, 50, { sort: '-created', filter: `customer_id = "${client.id}"` }).catch(() => ({ items: [] })),
                pb.collection('invoices').getList(1, 50, { sort: '-created', filter: `customer_id = "${client.id}"` }).catch(() => ({ items: [] })),
                pb.collection('projects').getList(1, 50, { sort: '-created', filter: `client = "${client.id}"` }).catch(() => ({ items: [] }))
            ]);
            setDetailStats({
                quotes: qRes?.items || [],
                invoices: iRes?.items || [],
                projects: pRes?.items || []
            });
        } catch (e) {
            console.error(e);
            setDetailStats({ quotes: [], invoices: [], projects: [] });
        }
    };

    const handleOpenDetail = (client) => {
        setSelectedClient(client);
        fetchClientDetails(client);
        setShowDetail(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            await pb.collection('customers').create({
                ...formData,
                created_by: user.id
            });
            setShowModal(false);
            setFormData({ name: '', email: '', phone: '', company: '', address: '', notes: '' });
            fetchClients();
        } catch (err) {
            console.error("Error creating client", err);
            alert("Erreur lors de la création du client");
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Supprimer ce client ?")) return;
        try {
            await pb.collection('customers').delete(id);
            fetchClients();
        } catch (err) {
            alert("Erreur suppression (il a peut-être des devis liés ?)");
        }
    };

    const filteredClients = clients.filter(c =>
        (c.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (c.company || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (c.email || '').toLowerCase().includes(searchTerm.toLowerCase())
    );

    const statusLabel = (s) => ({ draft: 'Brouillon', active: 'Actif', paused: 'En pause', done: 'Terminé' }[s] || '—');

    const calculateLeadScore = (client) => {
        let score = 0;
        if (client.email) score += 25;
        if (client.phone) score += 25;
        if (client.company) score += 20;
        if (client.address) score += 10;
        // Bonus for recency (created last 30 days)
        if (new Date(client.created) > new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)) score += 20;
        return score;
    };

    const getScoreLabel = (score) => {
        if (score >= 80) return { icon: '🔥', label: 'Hot', color: '#ef4444', bg: 'rgba(239, 68, 68, 0.1)' };
        if (score >= 50) return { icon: '⛅', label: 'Warm', color: '#f59e0b', bg: 'rgba(245, 158, 11, 0.1)' };
        return { icon: '❄️', label: 'Cold', color: '#3b82f6', bg: 'rgba(59, 130, 246, 0.1)' };
    };

    return (
        <CommercialLayout title="">
            <div className="commercial-page-wrapper">
                <style>{`
                /* Adapting Admin Styles to Commercial Layout - Scoped to avoid global leaks */
                .commercial-page-wrapper {
                    --admin-panel: var(--commercial-panel, #fff);
                    --admin-panel-2: var(--commercial-panel-2, #f9fafb);
                    --admin-border: var(--commercial-border, #e2e8f0);
                    --admin-text: var(--commercial-text, #0f172a);
                    --admin-muted: var(--commercial-muted, #64748b);
                    --admin-accent: var(--commercial-accent, #4f46e5);
                }
                
                .commercial-page-wrapper .btn-primary { 
                    display: inline-flex; align-items: center; gap: 10px;
                    padding: 10px 14px; border-radius: 10px;
                    background: linear-gradient(135deg, var(--admin-accent), rgba(79,70,229,0.85));
                    border: 1px solid rgba(79,70,229,0.6);
                    box-shadow: 0 6px 18px rgba(79,70,229,0.35);
                    color: white;
                    cursor: pointer;
                    transition: all 0.2s;
                    font-weight: 500;
                }
                .commercial-page-wrapper .btn-primary:hover { transform: translateY(-1px); filter: brightness(1.04); }
                .commercial-page-wrapper .btn-primary:active { transform: translateY(0); filter: brightness(0.98); }

                .commercial-page-wrapper .customer-card {
                    border: 1px solid var(--admin-border);
                    border-radius: 12px;
                    padding: 16px;
                    background: linear-gradient(180deg, var(--admin-panel), var(--admin-panel-2));
                    transition: transform 0.2s ease, box-shadow 0.2s ease;
                }
                .commercial-page-wrapper .customer-card:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 10px 25px -5px rgba(0,0,0,0.1);
                }

                .commercial-page-wrapper .customer-avatar {
                    width: 40px; height: 40px;
                    border-radius: 999px;
                    display: grid; place-items: center;
                    font-weight: 700;
                    background: var(--admin-accent);
                    color: white;
                    font-size: 1.1rem;
                }

                .commercial-page-wrapper .header-pill { cursor: pointer; border: none; background: transparent; color: var(--admin-text); padding: 6px; border-radius: 6px; transition: background 0.2s; }
                .commercial-page-wrapper .header-pill:hover { background: rgba(0,0,0,0.05); }
                .commercial-page-wrapper .header-shape { border-radius: 8px; padding: 6px 10px; border: 1px solid transparent; }
                
                .commercial-page-wrapper .btn-muted { background: var(--admin-panel); border: 1px solid var(--admin-border); }
                .commercial-page-wrapper .btn-muted:hover { border-color: var(--admin-muted); }
                
                .commercial-page-wrapper .btn-danger { background: rgba(239,68,68,0.1); color: #ef4444; border: 1px solid rgba(239,68,68,0.3); }
                .commercial-page-wrapper .btn-danger:hover { background: rgba(239,68,68,0.2); }

                .commercial-page-wrapper .searchbar { position: relative; display: flex; align-items: center; }
                .commercial-page-wrapper .searchbar .icon { position: absolute; left: 10px; color: var(--admin-muted); pointer-events: none; }
                .commercial-page-wrapper .searchbar input {
                    width: 300px;
                    background: var(--admin-panel);
                    border: 1px solid var(--admin-border);
                    color: var(--admin-text);
                    border-radius: 10px;
                    padding: 10px 10px 10px 36px;
                    outline: none;
                    transition: border-color 0.15s ease;
                }
                .commercial-page-wrapper .searchbar input:focus { border-color: var(--admin-accent); box-shadow: 0 0 0 3px rgba(79,70,229,0.1); }

                /* Modal */
                .commercial-page-wrapper .modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; z-index: 1001; padding: 20px; backdrop-filter: blur(2px); }
                .commercial-page-wrapper .modal-content { background: var(--admin-panel); color: var(--admin-text); border: 1px solid var(--admin-border); border-radius: 16px; width: 100%; max-width: 600px; max-height: 90vh; overflow-y: auto; box-shadow: 0 25px 50px -12px rgba(0,0,0,0.25); }
                .commercial-page-wrapper .modal-header { display: flex; justify-content: space-between; align-items: center; padding: 20px 24px; border-bottom: 1px solid var(--admin-border); }
                .commercial-page-wrapper .modal-header h3 { margin: 0; font-size: 1.25rem; font-weight: 600; }
                .commercial-page-wrapper .close-btn { background: none; border: none; cursor: pointer; color: var(--admin-muted); padding: 4px; border-radius: 4px; }
                .commercial-page-wrapper .close-btn:hover { background: rgba(0,0,0,0.05); color: var(--admin-text); }
                
                .commercial-page-wrapper .form-control { width: 100%; padding: 10px 12px; border-radius: 8px; border: 1px solid var(--admin-border); background: var(--admin-panel-2); color: var(--admin-text); font-size: 0.95rem; }
                .commercial-page-wrapper .form-control:focus { outline: none; border-color: var(--admin-accent); box-shadow: 0 0 0 3px rgba(79,70,229,0.1); }
                
                .commercial-page-wrapper .modal-actions { padding: 20px 24px; border-top: 1px solid var(--admin-border); display: flex; justify-content: flex-end; gap: 12px; }
                .commercial-page-wrapper .btn-secondary { background: transparent; border: 1px solid var(--admin-border); color: var(--admin-text); padding: 8px 16px; border-radius: 8px; cursor: pointer; font-weight: 500; }
                .commercial-page-wrapper .btn-secondary:hover { background: rgba(0,0,0,0.03); }

                .commercial-page-wrapper .btn-accent { background: rgba(79,70,229,0.12); color: var(--admin-accent); border: 1px solid rgba(79,70,229,0.45); }
                .commercial-page-wrapper .btn-accent:hover { background: rgba(79,70,229,0.22); border-color: rgba(79,70,229,0.6); }

                @media (max-width: 640px) {
                    .commercial-page-wrapper .searchbar input { width: 100%; }
                    .commercial-page-wrapper .header-top { flex-direction: column; align-items: stretch !important; gap: 16px; }
                    .commercial-page-wrapper .btn-primary { width: 100%; justify-content: center; }
                }
            `}</style>

                {/* Header Area */}
                <div className="header-top" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
                    <div>
                        <h1 style={{ margin: 0, fontSize: '1.8rem' }}>Mes Clients</h1>
                        <p style={{ color: 'var(--admin-muted)', margin: '4px 0 0 0' }}>Gérez votre portefeuille client</p>
                    </div>
                    <button
                        className="btn-primary"
                        onClick={() => { setFormData({ name: '', email: '', phone: '', company: '', address: '', notes: '' }); setShowModal(true); }}
                    >
                        <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 24, height: 24, borderRadius: 999, background: 'rgba(255,255,255,0.2)' }}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <line x1="12" y1="5" x2="12" y2="19"></line>
                                <line x1="5" y1="12" x2="19" y2="12"></line>
                            </svg>
                        </span>
                        <span>Nouveau client</span>
                    </button>
                </div>

                {/* Controls & Search */}
                <div className="card" style={{ marginBottom: 24, padding: 12, borderRadius: 12, border: '1px solid var(--admin-border)', background: 'var(--admin-panel)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
                        <div style={{ display: 'flex', gap: 8 }}>
                            <button className="header-pill header-shape" title="QR Onboarding" onClick={() => {
                                const link = window.location.origin + '/inscription?ref=' + user.id;
                                setQrUrl(link);
                                setShowQr(true);
                            }} style={{ border: '1px solid var(--admin-border)' }}>
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M3 3h8v8H3V3zm2 2v4h4V5H5zm8-2h8v8h-8V3zm2 2v4h4V5h-4zM3 13h8v8H3v-8zm2 2v4h4v-4H5zm12 0h2v2h-2v-2zm-4 0h2v2h-2v-2zm4 4h2v2h-2v-2zm-4 0h2v2h-2v-2z" />
                                </svg>
                            </button>
                            <button className="header-pill header-shape" style={{ border: '1px solid var(--admin-border)', fontSize: '0.9rem' }} onClick={async () => {
                                const link = window.location.origin + '/inscription?ref=' + user.id;
                                try { await navigator.clipboard.writeText(link); alert('Lien copié'); } catch { }
                            }}>Copier le lien</button>
                            <button className="header-pill header-shape" style={{ border: '1px solid var(--admin-border)', fontSize: '0.9rem' }} onClick={async () => {
                                const link = window.location.origin + '/inscription?ref=' + user.id;
                                const shareText = buildShareText(link);
                                try { await navigator.clipboard.writeText(shareText); alert('Message copié'); } catch { }
                            }}>Copier le message</button>
                            <button className="header-pill header-shape" style={{ border: '1px solid var(--admin-border)', fontSize: '0.9rem' }} onClick={async () => {
                                const link = window.location.origin + '/inscription?ref=' + user.id;
                                const shareText = buildShareText(link);
                                if (navigator.share) {
                                    try { await navigator.share({ title: 'Inscription', text: shareText }); } catch { }
                                } else {
                                    try { await navigator.clipboard.writeText(shareText); alert('Message copié'); } catch { alert('Partage non supporté'); }
                                }
                            }}>Partager</button>
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                            <div className="searchbar">
                                <svg className="icon" width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" /></svg>
                                <input
                                    type="text"
                                    placeholder="Rechercher un client..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                            </div>
                            <div style={{ fontSize: '0.9rem', color: 'var(--admin-muted)', whiteSpace: 'nowrap' }}>
                                {filteredClients.length} client{filteredClients.length > 1 ? 's' : ''}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Grid Content */}
                {loading ? (
                    <div style={{ padding: 40, textAlign: 'center', color: 'var(--admin-muted)' }}>Chargement...</div>
                ) : (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 16 }}>
                        {filteredClients.map(client => (
                            <div key={client.id} className="customer-card">
                                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                                    <div className="customer-avatar">
                                        {client.name.charAt(0).toUpperCase()}
                                    </div>
                                    <div style={{ minWidth: 0, flex: 1, textAlign: 'left' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                            <div style={{ fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontSize: '1.05rem', color: 'var(--admin-text)' }}>{client.name}</div>
                                            {(() => {
                                                const score = calculateLeadScore(client);
                                                const meta = getScoreLabel(score);
                                                return (
                                                    <div style={{ fontSize: '0.75rem', fontWeight: 700, padding: '2px 8px', borderRadius: 12, background: meta.bg, color: meta.color, display: 'flex', alignItems: 'center', gap: 4 }} title={`Score: ${score}/100`}>
                                                        <span>{meta.icon}</span> {meta.label}
                                                    </div>
                                                );
                                            })()}
                                        </div>
                                        {client.company && (
                                            <div style={{ color: 'var(--admin-accent)', fontSize: '0.85rem', fontWeight: 500 }}>{client.company}</div>
                                        )}
                                    </div>
                                </div>

                                <div style={{ display: 'grid', gap: 8, fontSize: '0.9rem', color: 'var(--admin-text)', marginBottom: 16 }}>
                                    {client.email && (
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" style={{ opacity: 0.6 }}><path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" /></svg>
                                            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{client.email}</span>
                                        </div>
                                    )}
                                    {client.phone && (
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" style={{ opacity: 0.6 }}><path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z" /></svg>
                                            <span>{client.phone}</span>
                                        </div>
                                    )}
                                </div>

                                <div style={{ paddingTop: 12, borderTop: '1px dashed var(--admin-border)', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                                    <button className="header-pill header-shape btn-muted" title="Voir les détails" onClick={() => handleOpenDetail(client)}>
                                        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z" /></svg>
                                            <span>Détails</span>
                                        </span>
                                    </button>
                                    <button className="header-pill header-shape btn-danger" title="Supprimer" onClick={() => handleDelete(client.id)}>
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" /></svg>
                                    </button>
                                </div>
                            </div>
                        ))}
                        {filteredClients.length === 0 && (
                            <div style={{ gridColumn: '1/-1', padding: 40, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, color: 'var(--admin-muted)' }}>
                                <svg width="48" height="48" viewBox="0 0 24 24" fill="currentColor" style={{ opacity: 0.3 }}><path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" /></svg>
                                <span>Aucun client trouvé pour cette recherche.</span>
                            </div>
                        )}
                    </div>
                )}

                {/* Create Client Modal */}
                {showModal && (
                    <div className="modal-overlay" onClick={() => setShowModal(false)}>
                        <div className="modal-content" onClick={e => e.stopPropagation()}>
                            <div className="modal-header">
                                <h3>Nouveau Client</h3>
                                <button className="close-btn" onClick={() => setShowModal(false)}>
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" /></svg>
                                </button>
                            </div>
                            <form onSubmit={handleSubmit} style={{ padding: '24px' }}>
                                <div style={{ display: 'grid', gap: 16 }}>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                                        <div>
                                            <label style={{ display: 'block', marginBottom: 6, fontWeight: 500, fontSize: '0.9rem' }}>Nom complet *</label>
                                            <input required className="form-control" value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} placeholder="Ex: Jean Dupont" />
                                        </div>
                                        <div>
                                            <label style={{ display: 'block', marginBottom: 6, fontWeight: 500, fontSize: '0.9rem' }}>Entreprise</label>
                                            <input className="form-control" value={formData.company} onChange={e => setFormData({ ...formData, company: e.target.value })} placeholder="Ex: Acme Corp" />
                                        </div>
                                    </div>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                                        <div>
                                            <label style={{ display: 'block', marginBottom: 6, fontWeight: 500, fontSize: '0.9rem' }}>Email</label>
                                            <input type="email" className="form-control" value={formData.email} onChange={e => setFormData({ ...formData, email: e.target.value })} />
                                        </div>
                                        <div>
                                            <label style={{ display: 'block', marginBottom: 6, fontWeight: 500, fontSize: '0.9rem' }}>Téléphone</label>
                                            <input className="form-control" value={formData.phone} onChange={e => setFormData({ ...formData, phone: e.target.value })} />
                                        </div>
                                    </div>
                                    <div>
                                        <label style={{ display: 'block', marginBottom: 6, fontWeight: 500, fontSize: '0.9rem' }}>Adresse</label>
                                        <input className="form-control" value={formData.address} onChange={e => setFormData({ ...formData, address: e.target.value })} placeholder="Adresse complète" />
                                    </div>
                                    <div>
                                        <label style={{ display: 'block', marginBottom: 6, fontWeight: 500, fontSize: '0.9rem' }}>Notes internes</label>
                                        <textarea className="form-control" rows={3} value={formData.notes} onChange={e => setFormData({ ...formData, notes: e.target.value })} />
                                    </div>
                                </div>
                                <div className="modal-actions" style={{ padding: '24px 0 0 0', marginTop: 24 }}>
                                    <button type="button" className="btn-secondary" onClick={() => setShowModal(false)}>Annuler</button>
                                    <button type="submit" className="btn-primary">Enregistrer</button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}

                {/* Client Detail Modal */}
                {showDetail && selectedClient && (
                    <div className="modal-overlay" onClick={() => { setShowDetail(false); setSelectedClient(null); }}>
                        <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 800 }}>
                            <div className="modal-header">
                                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                    <h3>{selectedClient.name}</h3>
                                    <span style={{ fontSize: '0.8rem', padding: '2px 8px', borderRadius: 12, background: 'var(--admin-panel-2)', border: '1px solid var(--admin-border)' }}>
                                        Timeline 360°
                                    </span>
                                </div>
                                <button className="close-btn" onClick={() => { setShowDetail(false); setSelectedClient(null); }}>
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" /></svg>
                                </button>
                            </div>

                            <div style={{ display: 'flex', borderBottom: '1px solid var(--admin-border)', padding: '0 24px' }}>
                                <button
                                    style={{ padding: '16px', background: 'none', border: 'none', borderBottom: activeTab === 'overview' ? '2px solid var(--admin-accent)' : '2px solid transparent', color: activeTab === 'overview' ? 'var(--admin-accent)' : 'var(--admin-muted)', fontWeight: 600, cursor: 'pointer' }}
                                    onClick={() => setActiveTab('overview')}
                                >
                                    Vue d'ensemble
                                </button>
                                <button
                                    style={{ padding: '16px', background: 'none', border: 'none', borderBottom: activeTab === 'timeline' ? '2px solid var(--admin-accent)' : '2px solid transparent', color: activeTab === 'timeline' ? 'var(--admin-accent)' : 'var(--admin-muted)', fontWeight: 600, cursor: 'pointer' }}
                                    onClick={() => setActiveTab('timeline')}
                                >
                                    Timeline
                                </button>
                            </div>

                            <div style={{ padding: 24, minHeight: 400 }}>
                                {activeTab === 'overview' ? (
                                    <>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 24 }}>
                                            <div className="customer-avatar" style={{ width: 64, height: 64, fontSize: '1.8rem' }}>
                                                {selectedClient.name.charAt(0).toUpperCase()}
                                            </div>
                                            <div>
                                                <div style={{ fontWeight: 700, fontSize: '1.4rem' }}>{selectedClient.name}</div>
                                                <div style={{ color: 'var(--admin-muted)' }}>{selectedClient.company || 'Particulier'}</div>
                                                <div style={{ fontSize: '0.85rem', color: 'var(--admin-muted)', marginTop: 4 }}>
                                                    Client depuis le {new Date(selectedClient.created).toLocaleDateString()}
                                                </div>
                                            </div>
                                        </div>

                                        <div style={{ display: 'grid', gap: 12, marginBottom: 24, padding: 16, background: 'var(--admin-panel-2)', borderRadius: 12, border: '1px solid var(--admin-border)' }}>
                                            <div style={{ fontWeight: 600, fontSize: '0.9rem', marginBottom: 4 }}>Coordonnées</div>
                                            {selectedClient.email && (
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" color="var(--admin-muted)"><path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" /></svg>
                                                    <a href={`mailto:${selectedClient.email}`} style={{ color: 'var(--admin-accent)', textDecoration: 'none' }}>{selectedClient.email}</a>
                                                </div>
                                            )}
                                            {selectedClient.phone && (
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" color="var(--admin-muted)"><path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z" /></svg>
                                                    <span>{selectedClient.phone}</span>
                                                </div>
                                            )}
                                            {selectedClient.address && (
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" color="var(--admin-muted)"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" /></svg>
                                                    <span>{selectedClient.address}</span>
                                                </div>
                                            )}
                                        </div>

                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
                                            <div style={{ border: '1px solid var(--admin-border)', borderRadius: 10, padding: 12, textAlign: 'center' }}>
                                                <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--admin-accent)' }}>{detailStats.projects.length}</div>
                                                <div style={{ fontSize: '0.8rem', color: 'var(--admin-muted)' }}>Projects</div>
                                            </div>
                                            <div style={{ border: '1px solid var(--admin-border)', borderRadius: 10, padding: 12, textAlign: 'center' }}>
                                                <div style={{ fontSize: '1.5rem', fontWeight: 700 }}>{detailStats.quotes.length}</div>
                                                <div style={{ fontSize: '0.8rem', color: 'var(--admin-muted)' }}>Devis</div>
                                            </div>
                                            <div style={{ border: '1px solid var(--admin-border)', borderRadius: 10, padding: 12, textAlign: 'center' }}>
                                                <div style={{ fontSize: '1.5rem', fontWeight: 700 }}>{detailStats.invoices.length}</div>
                                                <div style={{ fontSize: '0.8rem', color: 'var(--admin-muted)' }}>Factures</div>
                                            </div>
                                        </div>
                                    </>
                                ) : (
                                    <div className="timeline-container">
                                        <style>{`
                                            .timeline-item { display: flex; gap: 16px; padding-bottom: 24px; position: relative; }
                                            .timeline-item:last-child { padding-bottom: 0; }
                                            .timeline-line { position: absolute; left: 16px; top: 32px; bottom: 0; width: 2px; background: var(--admin-border); }
                                            .timeline-item:last-child .timeline-line { display: none; }
                                            .timeline-icon { width: 34px; height: 34px; border-radius: 50%; background: var(--admin-panel); border: 2px solid var(--admin-border); display: grid; place-items: center; z-index: 1; flex-shrink: 0; }
                                            .timeline-content { flex: 1; background: var(--admin-panel-2); border-radius: 12px; padding: 12px; border: 1px solid var(--admin-border); }
                                            .timeline-date { font-size: 0.8rem; color: var(--admin-muted); margin-bottom: 4px; }
                                        `}</style>

                                        {(() => {
                                            // Aggregate events
                                            const events = [
                                                ...detailStats.quotes.map(q => ({ type: 'quote', date: q.created, icon: '🧾', title: 'Devis créé', desc: `N° ${q.quote_number || '?'}` })),
                                                ...detailStats.invoices.map(i => ({ type: 'invoice', date: i.created, icon: '💶', title: 'Facture émise', desc: `N° ${i.invoice_number || '?'}` })),
                                                ...detailStats.projects.map(p => ({ type: 'project', date: p.created, icon: '🚀', title: 'Projet démarré', desc: p.name })),
                                                { type: 'create', date: selectedClient.created, icon: '👤', title: 'Nouveau Client', desc: 'Création de la fiche' }
                                            ].sort((a, b) => new Date(b.date) - new Date(a.date));

                                            return events.map((ev, i) => (
                                                <div key={i} className="timeline-item">
                                                    <div className="timeline-line"></div>
                                                    <div className="timeline-icon">{ev.icon}</div>
                                                    <div className="timeline-content">
                                                        <div className="timeline-date">{new Date(ev.date).toLocaleString()}</div>
                                                        <div style={{ fontWeight: 600 }}>{ev.title}</div>
                                                        <div style={{ fontSize: '0.9rem', color: 'var(--admin-muted)' }}>{ev.desc}</div>
                                                    </div>
                                                </div>
                                            ));
                                        })()}
                                    </div>
                                )}
                            </div>
                            <div className="modal-actions">
                                <button className="btn-secondary" onClick={() => { setShowDetail(false); setSelectedClient(null); }}>Fermer</button>
                            </div>
                        </div>
                    </div>
                )}
                {/* QR Modal */}
                {showQr && (
                    <div className="modal-overlay" onClick={() => setShowQr(false)}>
                        <div className="modal-content" style={{ maxWidth: 520 }} onClick={(e) => e.stopPropagation()}>
                            <div className="modal-header">
                                <h3>QR Inscription</h3>
                                <button className="close-btn" onClick={() => setShowQr(false)}>
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" /></svg>
                                </button>
                            </div>
                            <div style={{ padding: 20, display: 'grid', gap: 12, justifyItems: 'center' }}>
                                <img src={`https://api.qrserver.com/v1/create-qr-code/?size=220x220&data=${encodeURIComponent(qrUrl)}`} alt="QR Code" width={220} height={220} style={{ display: 'block', borderRadius: 12, border: '1px solid var(--admin-border)', background: 'white' }} />
                                <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
                                    <button className="header-pill header-shape btn-accent" onClick={async () => { try { await navigator.clipboard.writeText(qrUrl); alert('Lien copié'); } catch { } }}>Copier le lien</button>
                                    <button className="header-pill header-shape btn-secondary" onClick={() => { try { const a = document.createElement('a'); a.href = `https://api.qrserver.com/v1/create-qr-code/?size=220x220&data=${encodeURIComponent(qrUrl)}`; a.download = 'qr-inscription.png'; document.body.appendChild(a); a.click(); a.remove(); } catch { } }}>Télécharger QR</button>
                                </div>
                                <div style={{ wordBreak: 'break-all', color: 'var(--admin-muted)', textAlign: 'center', fontSize: '0.9rem' }}>{qrUrl}</div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </CommercialLayout>
    );
}
